import { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { pagePath } from '../lib/page-tree';
import { COLLAPSE_EVENT } from '../components/ui/SidebarPanel';

/**
 * Which maps of a tree are open (sidebar polish П9, «reveal» of VS Code): by default the path to the current map is open
 * and the rest folded; a twisty the GM clicks overrides it. «Свернуть все» (collapseSectionsIn(root, 'tree')) folds every
 * map of the tree inside `rootRef`.
 */
export function useTreeOpen(
  currentId: string | undefined,
  pages: readonly { readonly id: string; readonly parentPageId: string | null }[],
  rootRef: RefObject<HTMLElement | null>,
  /** Every map open until folded (the Подготовка rail: no «map in view» to reveal there). */
  defaultOpen = false,
): { readonly isOpen: (pageId: string) => boolean; readonly toggle: (pageId: string) => void } {
  const [chosen, setChosen] = useState<ReadonlyMap<string, boolean>>(new Map());
  const [allFolded, setAllFolded] = useState(false);
  const onPath = useMemo(() => {
    if (currentId === undefined) return new Set<string>();
    const path = pagePath(currentId, pages);
    return new Set(path.slice(0, -1).map((p) => p.id));
  }, [currentId, pages]);
  // A new current map reveals its path again.
  useEffect(() => setAllFolded(false), [currentId]);
  useEffect(() => {
    const onCollapse = (e: Event): void => {
      const detail = (e as CustomEvent<{ root: Element; scope: string }>).detail;
      if (detail.scope !== 'tree' || rootRef.current === null || !detail.root.contains(rootRef.current)) return;
      setChosen(new Map());
      setAllFolded(true);
    };
    window.addEventListener(COLLAPSE_EVENT, onCollapse);
    return () => window.removeEventListener(COLLAPSE_EVENT, onCollapse);
  }, [rootRef]);
  const isOpen = useCallback(
    (pageId: string): boolean => chosen.get(pageId) ?? (!allFolded && (defaultOpen || onPath.has(pageId))),
    [chosen, allFolded, onPath, defaultOpen],
  );
  const toggle = useCallback(
    (pageId: string): void => setChosen((prev) => new Map(prev).set(pageId, !isOpen(pageId))),
    [isOpen],
  );
  return { isOpen, toggle };
}
